<template>
  <div
    class="size-full overflow-hidden"
    :class="{ 'absolute inset-0': cover, relative: !cover }"
    :style="!cover ? `width: ${width ? `${width}px` : '100%'}` : ''"
  >
    <img
      alt=""
      aria-hidden="true"
      decoding="async"
      fetchpriority="low"
      :src="img(source, { width: 50 }, fallbackImageSettings)"
      class="relative inset-0 size-full"
      :class="[
        noScale ? 'object-contain' : 'scale-125 object-cover object-top',
      ]"
      style="filter: blur(24px)"
      :loading="lazy ? 'lazy' : 'eager'"
      :width="Number(width)"
      :height="Number(height)"
    />
    <img
      :alt="alt"
      decoding="async"
      :src="variantSizes.src"
      :loading="lazy ? 'lazy' : 'eager'"
      :srcset="variantSizes.srcset"
      :sizes="variantSizes.sizes"
      class="absolute inset-0 size-full"
      :class="noScale ? 'object-contain' : 'object-cover object-top'"
      :width="Number(width)"
      :height="Number(height)"
      :fetchpriority="highPriority ? 'high' : 'auto'"
      @load="emit('load')"
    />
  </div>
</template>

<script setup lang="ts">
import type { Link } from '@unhead/vue'
import {
  isSvg,
  fallbackImageSettings,
  defaultSizesOptimised,
  notUpscaledDefaultSizes,
} from '~/composables/useSbImage'

type HeroImageProps = {
  source: string
  optimiseDesktop?: boolean
  quality?: number
  alt?: string
  title?: string
  noScale?: boolean
  cover?: boolean
  lazy?: boolean
  highPriority?: boolean
  media?: 'all' | 'desktop' | 'mobile'
}

const props = withDefaults(defineProps<HeroImageProps>(), {
  optimiseDesktop: false,
  quality: 70,
  alt: undefined,
  title: undefined,
  noScale: false,
  cover: false,
  lazy: true,
  highPriority: false,
  media: 'all',
})

const emit = defineEmits(['load'])
const route = useRoute()
const img = useImage()
const provider = 'storyblok'
const mainImageModifiers = {
  format: 'webp',
  quality: props.quality,
}

const sizes = computed(() => {
  if (provider !== 'storyblok') {
    return null
  }
  const dimensions = props.source?.split('/')?.[5]?.split('x')
  if (!dimensions) {
    return null
  }
  const [imageWidth, imageHeight] = dimensions
  if (!imageWidth || !imageHeight) {
    return null
  }

  // fallback - storyblok only allows 4000x4000 images max
  if (parseInt(imageHeight, 10) > 4000 || parseInt(imageWidth, 10) > 4000) {
    return null
  }

  return [imageWidth, imageHeight]
})

const width = computed(() => {
  if (!sizes.value) {
    return null
  }
  return sizes.value[0]
})

const height = computed(() => {
  if (!sizes.value) {
    return null
  }
  return sizes.value[1]
})

const imageSizes = computed(() => {
  if (props.optimiseDesktop) {
    return defaultSizesOptimised
  }
  const fallbackWidth = width.value ? parseInt(width.value, 10) : 4000
  return notUpscaledDefaultSizes(fallbackWidth)
})

const variants = computed(() => {
  const opts = {
    sizes: imageSizes.value,
    provider,
    modifiers: {
      quality: mainImageModifiers.quality,
      width: parseSize(width.value),
      height: parseSize(height.value),
    },
  }
  const v = getVariants(img, props.source, opts)
  return v.map((i) => {
    i.src = img(
      props.source,
      { ...opts.modifiers, width: i.width, height: 950 },
      opts,
    )
    return i
  })
})

const variantSizes = computed(() => getSizes(variants.value))
const isHomepage = computed(() => route.meta?.pageType === 'home')
const shouldSkipHomepagePreload = computed(
  () => isHomepage.value && !props.highPriority,
)

const preload = computed(() => {
  if (shouldSkipHomepagePreload.value || !props.source || isSvg(props.source)) {
    return []
  }
  let media: string
  if (!props.media || props.media === 'all') {
    media = 'all'
  } else if (props.media === 'desktop') {
    media = '(min-width: 640px)'
  } else {
    media = '(max-width: 639px)'
  }
  return [
    {
      hid: props.source,
      rel: 'preload',
      as: 'image',
      imagesrcset: variantSizes.value.srcset,
      imagesizes: variantSizes.value.sizes,
      media,
    },
  ]
})

useHead({ link: preload.value as Link[] })
</script>
